import { useAnchorWallet, useWallet } from "@solana/wallet-adapter-react";
import {
  WalletModalProvider,
  WalletMultiButton,
} from "@solana/wallet-adapter-react-ui";
import React, {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { ReactComponent as plusIcon } from "../../assets/add.svg";
import monkeLogo from "../../assets/poweredByUnique.svg";
import { isMobile } from "react-device-detect";
import { HOME } from "../../common/constants/routes.constants";
import classes from "./Navbar.module.scss";
import NetworkSettings from "./NetworkSettings/NetworkSettings";
import { BiMenu as HamburgerMenu } from "react-icons/bi";
import copy from "../../assets/copyWallet.svg";
import { createNotification } from "../../utilities/notification";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
} from "../../common/constants/common.constants";
import { UserContext } from "../../contexts/userContext";
import {
  checkIfUserCanCreateClub,
  getAdminData,
  getWhitelistedUsers,
  socket,
} from "../../solana/common/helpers/common.helpers";
import { getAllNotifications } from "../../api/notifications.api";
import { menuItems } from "../../common/constants/dashboard.constants";
import { dashboardStore } from "../../contexts/dashboardStore";
import { DashboardActiveStep } from "../../common/enums/dashboard.enums";
import NotificationWrapper from "./NotificationComponent/NotificationWrapper";
import { getUserRequest } from "../../api/users.api";
const REACT_APP_HIDE_BROWSE_CLUBS = process.env.REACT_APP_HIDE_BROWSE_CLUBS;

interface NavbarLink {
  href: string;
  name: string;
  icon?: any;
  onClick?: (e: SyntheticEvent) => void;
}

const Navbar: React.FC = () => {
  const [isSidebarOpen, toggleSidebar] = useState(false);
  const [navbarColor, setNavbarColor] = useState("transparent");
  const searchParams = useLocation();

  const { user, adminData, toggleIsWhitelisted, setAdminData, updateUser } =
    useContext(UserContext);
  const {
    notifications,
    setNotifications,
    isNotificationsModalVisible,
    toggleNotificationsModal,
    isXnft,
    setIsXnft,
  } = useContext(UserContext);

  const navigate = useNavigate();
  const wallet = useWallet();

  const { setActiveStep, activeStep, initialLoading } = dashboardStore();

  //TODO@milica-monke-merge: For monke create club is visible only if user is connected and able to create club
  const links = useMemo(() => {
    const linksArray: NavbarLink[] = [];
    linksArray.push({ href: "dashboard", name: "DASHBOARD" });
    if (REACT_APP_HIDE_BROWSE_CLUBS !== "true") {
      linksArray.push({ href: EMPTY_STRING, name: "BROWSE CLUBS" });
    }
    if (checkIfUserCanCreateClub(wallet.publicKey, adminData)) {
      linksArray.push({
        href: "create-club",
        name: "CREATE CLUB",
        icon: plusIcon,
      });
    }
    return linksArray;
  }, [wallet, adminData]);

  useEffect(() => {
    if (searchParams.search.includes("isXNFT=true")) {
      setIsXnft(true);
    }
  }, []);

  useEffect(() => {
    if (!wallet?.publicKey) {
      setNotifications([]);
      socket.disconnect();
      socket.close();
    }
    if (wallet && wallet.publicKey) {
      if (!user) void getUserData();
      void saveUserNotifications();
      socket.connect().emit("subscribeUser", wallet.publicKey.toBase58());
    }
  }, [wallet?.publicKey]);

  const getUserData = async () => {
    try {
      if (wallet && wallet.publicKey) {
        const whitelistedMembers = await getWhitelistedUsers();
        const isWhit = !!whitelistedMembers.find(
          (member) => member.toString() === wallet.publicKey!.toString()
        );
        toggleIsWhitelisted(isWhit);
        setAdminData(await getAdminData(wallet!));
        const userData = await getUserRequest(wallet.publicKey!.toString());

        if (userData) {
          updateUser(userData);
        }
      }
    } catch (error) {}
  };

  const saveUserNotifications = async () => {
    try {
      const notifications = await getAllNotifications(
        wallet.publicKey!.toString()
      );

      setNotifications(notifications);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Failed to get notifications");
    }
  };

  const listenToScroll = (event) => {
    if (window.scrollY > 10) {
      setNavbarColor("#0c121d");
    } else {
      setNavbarColor("transparent");
    }
  };

  const copyWalletAddress = () => {
    try {
      if (wallet) {
        navigator.clipboard.writeText(wallet.publicKey!.toString());
        createNotification(MESSAGE_TYPE.SUCCESS, "Wallet address copied!");
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Wallet address copying failed!");
    }
  };

  const walletButtonClassName = wallet.connecting
    ? classes.walletButtonConnecting
    : !wallet.connected
    ? classes.walletButton
    : classes.walletButtonDisconnected;

  useEffect(() => {
    window.addEventListener("scroll", listenToScroll);
  });

  return (
    <WalletModalProvider>
      <div className={classes.navbarBanner}>
        <div
          className={classes.navbarContainer}
          style={{ backgroundColor: navbarColor }}
        >
          {isSidebarOpen && (
            <div
              className={classes.backdrop}
              onClick={() => toggleSidebar(false)}
            />
          )}
          <div className={classes.navbarWrapper}>
            {/*TODO@milica-monke-merge: For monke - monke logo, for main unq */}
            <img
              onClick={() =>
                !isXnft
                  ? navigate(HOME)
                  : setActiveStep(DashboardActiveStep.Dashboard)
              }
              className={classes.logo}
              src={'/logo.svg'}
              alt="Unique logo"
            />

            <div className={classes.navbarMobileWrapper}>
              <HamburgerMenu
                color="white"
                size={30}
                onClick={() => toggleSidebar(true)}
              />
              <NotificationWrapper />
            </div>
          </div>
          <div
            className={
              isSidebarOpen
                ? `${classes.menuContainer} ${classes.menuContainerOpen}`
                : classes.menuContainer
            }
          >
            <ul
              className={`${
                !isSidebarOpen ? classes.navbarUl : classes.navbarUlMobile
              }`}
            >
              {!isXnft &&
                links.map((item) => (
                  <li
                    className={
                      item.icon ? classes.createClub : classes.browseClubs
                    }
                    key={item.name}
                  >
                    {item.icon && <item.icon />}
                    <Link
                      to={`/${item.href}`}
                      onClick={(e) => {
                        item.onClick && item.onClick(e);
                        isSidebarOpen && toggleSidebar(false);
                      }}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              {isXnft &&
                isSidebarOpen &&
                menuItems.map((item) => {
                  return (
                    <li
                      className={
                        activeStep === item.step
                          ? classes.xnftMenuItemActive
                          : classes.xnftMenuItem
                      }
                      key={item.title}
                    >
                      {item.icon && <item.icon />}
                      <div
                        onClick={(e) => {
                          !initialLoading && setActiveStep(item.step);
                          isSidebarOpen && toggleSidebar(false);
                        }}
                      >
                        {item.title}
                      </div>
                    </li>
                  );
                })}
            </ul>
            <div className={classes.menuContainerActions}>
              <div className={classes.walletButtonWrapper}>
                <WalletMultiButton className={walletButtonClassName} />
                {wallet.connected && (
                  <img
                    onClick={copyWalletAddress}
                    className={classes.copyWallet}
                    src={copy}
                    alt="copyPubkey"
                  />
                )}
              </div>
              <NotificationWrapper />

              {!isXnft && (
                <NetworkSettings
                  closeSideBar={() => {
                    isSidebarOpen && toggleSidebar(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </WalletModalProvider>
  );
};

export default Navbar;
